import { jsx } from "react/jsx-runtime";
import { JobDetailsView } from "../JobDetailsView/JobDetailsView.mjs";
import { BraidPortal, Box } from "braid-design-system";
import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { frame, styles } from "./JobDetailsPreview.css.mjs";
function throttle(func, timeFrame) {
  let lastTime = 0;
  return function(...args) {
    const now = (/* @__PURE__ */ new Date()).getMilliseconds();
    const l = now - lastTime;
    if (l >= timeFrame) {
      func(...args);
      lastTime = now;
    }
  };
}
const useThrottledResize = (onResize, delay = 100) => {
  const throttledResize = useMemo(
    () => throttle(onResize, delay),
    [onResize, delay]
  );
  useEffect(() => {
    window.addEventListener("resize", throttledResize);
    return () => {
      window.removeEventListener("resize", throttledResize);
    };
  }, [throttledResize]);
};
const collectParentStyles = ({ onComplete }) => {
  const stylingNodes = [];
  const styleTags = Array.from(
    document.querySelectorAll("style")
  );
  styleTags.forEach((style) => {
    stylingNodes.push(style.cloneNode(true));
  });
  const linkTags = Array.from(
    document.querySelectorAll("link[rel=stylesheet]")
  );
  const stylesheetReadyState = linkTags.reduce(
    (acc, link) => {
      const key = link.getAttribute("href");
      return {
        ...acc,
        ...key !== null ? { [key]: false } : {}
      };
    },
    {}
  );
  linkTags.forEach((link) => {
    const linkNode = link.cloneNode();
    linkNode.onload = () => {
      const key = link.getAttribute("href");
      if (key) {
        stylesheetReadyState[key] = true;
      }
      if (Object.values(stylesheetReadyState).every((v) => v)) {
        onComplete();
      }
    };
    stylingNodes.push(linkNode);
  });
  return stylingNodes;
};
const IFrame = (props) => {
  const { children, sizeToContent = false, title, className } = props;
  const container = useRef(null);
  const frame2 = useRef(null);
  const [documentPrepared, setDocumentPrepared] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [contentHeight, setContentHeight] = useState(null);
  const updateHeightOnEvent = useCallback(() => {
    if (container.current) {
      setContentHeight(container.current.offsetHeight);
    }
  }, [container, setContentHeight]);
  useThrottledResize(updateHeightOnEvent, 40);
  const onIframeLoad = useCallback(() => {
    updateHeightOnEvent();
    setShowContent(true);
  }, [updateHeightOnEvent]);
  useEffect(() => {
    if (frame2.current?.contentDocument?.head) {
      const stylingNodes = collectParentStyles({
        onComplete: () => setDocumentPrepared(true)
      });
      frame2.current.contentDocument.head.append(...stylingNodes);
    }
  }, [frame2.current?.contentDocument?.head]);
  return /* @__PURE__ */ jsx(
    "iframe",
    {
      srcDoc: "<!DOCTYPE html>",
      title,
      height: sizeToContent && contentHeight ? `${contentHeight}px` : void 0,
      onLoad: onIframeLoad,
      ref: frame2,
      className,
      children: documentPrepared ? /* @__PURE__ */ jsx(BraidPortal, { container: frame2.current?.contentDocument?.body, children: /* @__PURE__ */ jsx(
        Box,
        {
          ref: container,
          pointerEvents: "none",
          opacity: !showContent ? 0 : void 0,
          transition: "fast",
          children
        }
      ) }) : null
    }
  );
};
const JobDetailsPreview = (props) => {
  const { layout = "desktop", title, ...spread } = props;
  return /* @__PURE__ */ jsx(
    IFrame,
    {
      className: `${frame} ${styles[layout]}`,
      sizeToContent: layout === "desktop",
      title,
      children: /* @__PURE__ */ jsx(JobDetailsView, { ...spread })
    }
  );
};
export {
  JobDetailsPreview
};
